import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useLanguage } from '../context/LanguageProvider/LanguageProvider';
import { SHOWCASE_PROJECT_IDS, useAdminData } from '../context/AdminDataProvider/AdminDataProvider';

const ADMIN_PROJECT_LOGO_FALLBACK = '/images/degital.png';

const ADMIN_SEED_IDS_HIDDEN_IN_PORTFOLIO = SHOWCASE_PROJECT_IDS;

const PORTFOLIO_DEF_ID_TO_SEED_ID = {
  morediet: 'p_seed_1',
  medagricycle: 'p_seed_3',
  techoney: 'p_seed_2',
};

const FILTER_DEFS = [
  { id: 'all', msgKey: 'portfolio.filter.all' },
  { id: 'observatories', msgKey: 'portfolio.filter.observatories' },
  { id: 'blockchain', msgKey: 'portfolio.filter.blockchain' },
  { id: 'research', msgKey: 'portfolio.filter.research' },
  { id: 'saas', msgKey: 'portfolio.filter.saas' },
  { id: 'mobile', msgKey: 'portfolio.filter.mobile' },
];

const PROJECT_DEFS = [
  {
    id: 'morediet',
    filterIds: ['observatories', 'blockchain', 'research', 'saas'],
    logoSrc: '/images/moremediet.png',
    tags: ['FoodTraceability', 'PRIMA', 'Observatory', 'ConsumerEngagement'],
    websiteUrl: 'https://nft.mmd-observatory.com/',
  },
  {
    id: 'medagricycle',
    filterIds: ['observatories', 'research', 'saas', 'mobile'],
    logoSrc: '/images/MedAgricycle.png',
    tags: ['CircularEconomy', 'IoT', 'Sustainability', 'LivingLab'],
    websiteUrl: 'https://medagricycle.mmd-observatory.com',
  },
  {
    id: 'techoney',
    filterIds: ['blockchain', 'saas'],
    logoSrc: '/images/techoney.png',
    tags: ['Blockchain', 'Traceability', 'AgriTech', 'E-commerce'],
    websiteUrl: 'https://www.techoney.org/fr/',
  },
];

export function usePortfolioProjects() {
  const { t } = useLanguage();
  const { domains, projects: adminProjects } = useAdminData();
  const [searchParams] = useSearchParams();
  const [activeFilter, setActiveFilter] = useState('all');

  const filters = useMemo(() => {
    const items = [{ id: 'all', label: t('portfolio.filter.all') }];
    const slugsFromDomains = new Set(domains.map((d) => d.slug));
    domains.forEach((d) => {
      items.push({ id: d.slug, label: d.name });
    });
    FILTER_DEFS.forEach((f) => {
      if (f.id === 'all') return;
      if (!slugsFromDomains.has(f.id)) {
        items.push({ id: f.id, label: t(f.msgKey) });
      }
    });
    return items;
  }, [domains, t]);

  useEffect(() => {
    const filter = searchParams.get('filter');
    if (!filter) return;
    if (filters.some((f) => f.id === filter)) {
      setActiveFilter(filter);
    }
  }, [searchParams, filters]);

  const staticProjects = useMemo(
    () =>
      PROJECT_DEFS.map((def) => {
        const seedId = PORTFOLIO_DEF_ID_TO_SEED_ID[def.id];
        const ap = seedId ? adminProjects.find((x) => x.id === seedId) : null;
        const domain = ap ? domains.find((d) => d.id === ap.domainId) : null;
        const adminUrl = (ap?.websiteUrl || '').trim();
        const defUrl = (def.websiteUrl || '').trim();
        const slug = domain?.slug;
        const filterIds =
          slug && !def.filterIds.includes(slug) ? [...new Set([...def.filterIds, slug])] : def.filterIds;
        return {
          ...def,
          filterIds,
          logoSrc: (ap?.logoUrl || '').trim() || def.logoSrc,
          photoUrl: (ap?.photoUrl || '').trim(),
          logoAlt: t(`portfolio.project.${def.id}.logoAlt`),
          title: (ap?.name || '').trim() || t(`portfolio.project.${def.id}.title`),
          /** Card copy: use catalogue blurb (admin default is a one-line summary only). */
          description: t(`portfolio.project.${def.id}.description`),
          role: t(`portfolio.project.${def.id}.role`),
          technologies: t(`portfolio.project.${def.id}.tech`),
          websiteUrl: adminUrl || defUrl,
        };
      }),
    [t, adminProjects, domains],
  );

  const adminPortfolioCards = useMemo(
    () =>
      adminProjects
        .filter((p) => !ADMIN_SEED_IDS_HIDDEN_IN_PORTFOLIO.has(p.id))
        .map((p) => {
          const domain = domains.find((d) => d.id === p.domainId);
          const slug = domain?.slug;
          const statusLabel = t(`admin.crud.projectStatus.${p.status || 'active'}`);
          const technologies = domain ? `${domain.name} · ${statusLabel}` : statusLabel;
          const tags = [...new Set([...(domain ? [domain.slug] : []), 'JASSP'])];
          return {
            id: `admin-${p.id}`,
            filterIds: slug ? [slug] : [],
            logoSrc: p.logoUrl || ADMIN_PROJECT_LOGO_FALLBACK,
            photoUrl: (p.photoUrl || '').trim(),
            logoAlt: p.name,
            title: p.name,
            description: (p.description || '').trim() || t('realisations.card.genericDescription'),
            role: t('portfolio.adminManaged.role'),
            technologies,
            tags,
            websiteUrl: (p.websiteUrl || '').trim(),
          };
        }),
    [adminProjects, domains, t],
  );

  const projects = useMemo(() => [...staticProjects, ...adminPortfolioCards], [staticProjects, adminPortfolioCards]);

  const visibleProjects = useMemo(() => {
    if (activeFilter === 'all') return projects;
    return projects.filter((p) => p.filterIds.includes(activeFilter));
  }, [activeFilter, projects]);

  return { filters, activeFilter, setActiveFilter, projects, visibleProjects };
}
